import {
  ButtonDS,
  DialogModalDS,
  DownloadOutlined,
  Spacer,
  TextCapitalized,
  dateFormatterDayMonthYearLong2Digits,
  dateFormatterHourMinute,
  uppercaseFirstLetter,
} from "@qivia/ui";
import { createFileXlsx } from "@qivia/ui/src/designSystem/components/materialUiTable/createFileXlsx";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import styled from "styled-components";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import { useAppDispatch } from "../../../../redux/hooks";
import { DriversHistory } from "../../fleet/fleetApi";

export type DriverHistoryRow = {
  analyticCode1: string;
  lastName: string;
  firstName: string;
  startOfUsage: string;
  startOfUsageTime: string;
  endOfUsage: string;
  endOfUsageTime: string;
};

export const DriverHistoryModal = (props: {
  visible: boolean;
  onClose: () => void;
  driversHistoryList: DriversHistory[];
}) => {
  const pageName = "vehicles";
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const driversHistoryExcelColumns = useMemo(
    () =>
      [
        "analyticCode1",
        "lastName",
        "firstName",
        "startOfUsage",
        "startOfUsageTime",
        "endOfUsage",
        "endOfUsageTime",
      ] as (keyof DriverHistoryRow)[],
    [],
  );

  const driversHistoryRows = useMemo(
    () =>
      props.driversHistoryList.map((dh) => ({
        analyticCode1: dh.driverInfo.analyticCode1 ?? "",
        lastName: dh.driverInfo.lastName,
        firstName: dh.driverInfo.firstName,
        startOfUsage: dateFormatterDayMonthYearLong2Digits(
          new Date(dh.startOfUsage),
        ),
        startOfUsageTime: dateFormatterHourMinute(new Date(dh.startOfUsage)),
        endOfUsage: dh.endOfUsage
          ? dateFormatterDayMonthYearLong2Digits(new Date(dh.endOfUsage))
          : "",
        endOfUsageTime: dh.endOfUsage
          ? dateFormatterHourMinute(new Date(dh.endOfUsage))
          : "",
      })),
    [props.driversHistoryList],
  );

  const handleDownloadDriverHistory = () => {
    if (driversHistoryRows.length <= 0) return;
    const headerTitles = driversHistoryExcelColumns.map((key) =>
      uppercaseFirstLetter(t(`driversHistory.column.${key}`)),
    );

    const file = createFileXlsx({
      tabTitle: t(`${pageName}.view.title`),
      headerTitles,
      body: driversHistoryRows.map((driverHistory) =>
        driversHistoryExcelColumns.map((column) => driverHistory[column]),
      ),
    });

    if (!file) return;
    void dispatch(
      documentDownloadXlsxAsync({
        buffer: file,
        fileName: "QIVIA_" + t("vehicles.driversHistory.excel.title") + ".xlsx",
      }),
    );
  };

  return (
    <DialogModalDS
      visible={props.visible}
      onClose={props.onClose}
      title={t("vehicles.sidepanel.driverHistory")}
      width={72}
    >
      <StyledModalContent>
        <StyledTableWrapper>
          <StyledTable>
            <thead>
              <tr>
                {driversHistoryExcelColumns.map((column) => (
                  <th key={column}>
                    <TextCapitalized>
                      {t(`driversHistory.column.${column}`)}
                    </TextCapitalized>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {driversHistoryRows.map((driverHistory) => (
                <tr
                  key={`${driverHistory.analyticCode1}-${driverHistory.startOfUsage}`}
                >
                  {driversHistoryExcelColumns.map((column) => (
                    <td key={column}>{driverHistory[column]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </StyledTableWrapper>
        <Spacer y={1.5} />
        <ButtonDS
          sizeButton="S"
          format="fill"
          buttonType="secondary"
          text={t("vehicles.sidepanel.downloadDriversHistoryList")}
          leftIcon={<DownloadOutlined />}
          onClick={handleDownloadDriverHistory}
        />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  width: 100%;
`;

const StyledTableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  ${typographies["Body/S"]}
  color: ${colors["colors/text/black"]};

  th,
  td {
    border: 0.0625rem solid ${colors["colors/borders/cells/cells"]};
    padding: 0.75rem;
    text-align: left;
  }

  th {
    color: ${colors["colors/text/darkGrey"]};
    font-weight: 600;
  }
`;
